import BotAvatar from '../BotAvatar';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import PatientChat from '../PatientChat';
import './MitraAssistant.css';

const ROLES = ['admin', 'superadmin', 'cm', 'rcm', 'sm', 'rsm'];
export function canUseAssistant(user) {
  return !!user && ROLES.includes(String(user.role || '').toLowerCase());
}
export function readChatMetadata(data) {
  if (data?.contract_version !== 3 || !Array.isArray(data.fields) || !Array.isArray(data.date_fields)) {
    throw new Error('Unsupported chat metadata');
  }
  return {fields: data.fields, dateFields: data.date_fields, scope: data.scope || '', patientReadReady: data.patient_read_version === 1};
}

export async function loadChatMetadata(api, signal) {
  const {data} = await api.get('/mitracare-ai/database/metadata', {signal, timeout:30000});
  return readChatMetadata(data);
}

function AssistantDialog({ authAxios, onClose }) {
  const [meta, setMeta] = useState(null), [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const panel = useRef(null), close = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    setMeta(null); setError('');
    loadChatMetadata(authAxios,controller.signal)
      .then(data => {if (!controller.signal.aborted) setMeta(data);})
      .catch(err => {if (!controller.signal.aborted) setError(err.response?.data?.message || 'Unable to load Ask MitraCare. Please retry.');});
    return () => controller.abort();
  }, [authAxios, attempt]);
  useEffect(() => {
    close.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function keydown(event) {
      if (event.key === 'Escape') {event.preventDefault(); onClose(); return;}
      if (event.key !== 'Tab') return;
      const items = [...(panel.current?.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') || [])].filter(el => el.getClientRects().length);
      if (!items.length) {event.preventDefault(); return;}
      const first=items[0], last=items[items.length-1];
      if (event.shiftKey && (document.activeElement===first || !panel.current?.contains(document.activeElement))) {event.preventDefault(); last.focus();}
      else if (!event.shiftKey && (document.activeElement===last || !panel.current?.contains(document.activeElement))) {event.preventDefault(); first.focus();}
    }
    document.addEventListener('keydown',keydown);
    return () => {document.removeEventListener('keydown',keydown); document.body.style.overflow=previousOverflow;};
  }, [onClose]);
  return <div className="mitra-ai-overlay" onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
    <section ref={panel} className="mitra-ai-panel" role="dialog" aria-modal="true" aria-labelledby="mitra-assistant-title">
      <header className="mitra-ai-header"><div id="mitra-assistant-avatar" aria-hidden="true" style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', marginRight: 12 }} /><div><h2 id="mitra-assistant-title">Ask MitraCare AI</h2><p>Answers from records available to your account</p></div><button ref={close} type="button" onClick={onClose} aria-label="Close Ask MitraCare">×</button></header>
      <div className="mitra-ai-panel-body">
        {!meta && !error && <p role="status">Loading your available chat tools…</p>}
        {error && <div role="alert"><p>{error}</p><button type="button" onClick={()=>setAttempt(v=>v+1)}>Retry</button></div>}
        {meta && <><p className="mitra-ai-scope">Scope: {meta.scope}</p><PatientChat authAxios={authAxios} ready={true} fields={meta.fields} dateFields={meta.dateFields} patientReadReady={meta.patientReadReady} embedded /></>}
      </div>
      <footer className="mitra-ai-footer">Closing this chat clears its conversation. <a href="#/mitracare-database" onClick={onClose}>Open database tools</a></footer>
    </section>
  </div>;
}

function AssistantSession({authAxios}) {
  const [open,setOpen]=useState(false);
  const launcher=useRef(null);
  const close=React.useCallback(()=>{setOpen(false); requestAnimationFrame(()=>launcher.current?.focus());},[]);
  return <div className="mitra-ai-root">
    <button ref={launcher} type="button" className="mitra-ai-launch mitra-ai-launch--avatar" aria-haspopup="dialog" aria-expanded={open} onClick={()=>setOpen(true)} hidden={open} aria-label="Open MitraCare AI assistant" title="Open MitraCare AI assistant"><BotAvatar state="idle" size={68} /></button>
    {open && <AssistantDialog authAxios={authAxios} onClose={close} />}
  </div>;
}
export default function MitraAssistant() {
  const {user,authAxios,sessionId}=useContext(AuthContext);
  if (!canUseAssistant(user) || !authAxios) return null;
  return <AssistantSession key={`${user.id || user.sub}:${String(user.role).toLowerCase()}:${sessionId || user.sess || user.session_id || ''}`} authAxios={authAxios} />;
}
